"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/app/lib/reducers/api";

// Devis du tunnel de paiement, calcule par le SERVEUR (POST /api/checkout/quote) : options du
// tour, code promo revalide, pieces Guydeez detenues, table de taux fixe. C'est ce montant que
// le tunnel affiche et que la carte paie, au centime (CTO, 2026-10-22) — le navigateur ne
// calcule plus le montant du.
export type CheckoutQuote = {
  ok: true;
  currency: string;
  decimals: number;
  coinsUsed: number;
  subtotalEUR: number;
  discountEUR: number;
  coinsEUR: number;
  amountDueEUR: number;
  subtotal: number;
  discount: number;
  coins: number;
  amountDue: number;
  amountDueMinor: number;
};

export type QuoteRefusal = {
  ok: false;
  code: "cart_invalid" | "unpriced_cart" | "unsupported_currency" | "discount_reserved" | "currency_required" | string;
};

type Params = {
  cartUuids: string[];
  discountUuid: string | null;
  coinsUsed: number;
  currency: string;
  enabled?: boolean;
};

export function useCheckoutQuote({ cartUuids, discountUuid, coinsUsed, currency, enabled = true }: Params) {
  const [quote, setQuote] = useState<CheckoutQuote | null>(null);
  const [refusal, setRefusal] = useState<QuoteRefusal | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Seule la derniere demande compte : une reponse lente ne doit pas ecraser un devis plus recent
  const requestId = useRef(0);
  const cartsKey = cartUuids.join(",");

  const fetchQuote = useCallback(async () => {
    if (!enabled || !cartUuids.length || !currency) {
      setQuote(null);
      setRefusal(null);
      return null;
    }
    const id = ++requestId.current;
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await api.post("/api/checkout/quote", {
        cartItems: cartUuids,
        discountUuid: discountUuid || null,
        coinsUsed: Math.max(0, Math.floor(coinsUsed || 0)),
        currency,
      });
      if (id !== requestId.current) return null;
      setQuote(data as CheckoutQuote);
      setRefusal(null);
      return data as CheckoutQuote;
    } catch (error: any) {
      if (id !== requestId.current) return null;
      const body = error?.response?.data;
      setQuote(null);
      if (body && body.ok === false) setRefusal(body as QuoteRefusal);
      else setFailed(true);
      return null;
    } finally {
      if (id === requestId.current) setLoading(false);
    }
    // cartsKey resume cartUuids (nouveau tableau a chaque rendu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, cartsKey, discountUuid, coinsUsed, currency]);

  // Petit delai : le curseur des pieces et la saisie du code promo ne lancent qu'une demande
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuote();
    }, 150);
    return () => clearTimeout(timer);
  }, [fetchQuote]);

  return { quote, refusal, loading, failed, refresh: fetchQuote };
}
